/**
=== FILE HEADER: Tile.js ===
Description: Portal-based rendering for chess tile and piece overlays. Supports dynamic CSS module switching for PC and mobile.

=== COPILOT FILE RULES ===
DO NOT DELETE THE HEADER OR FILE RULES FROM THE FILE
DO NOT PLACE ANY CODE, VARIABLES, OR LOGIC ABOVE THIS HEADER.
All import statements must be at the very top of the file, after this header/rules block.
Make a copy of the file in copies folder before doing any edits.
Updating full large files has been difficult. You may use the copy to refer to if it helps you.
After completing edits compare the edited file to the original and make sure nothing other than the intended changes have happened.
If there are unintended changes repair the file.
After user approves the copy may be deleted.
DO NOT USE INLINE STYLES; ALL STYLES MUST BE DEFINED IN THE PROPER FILE AND IMPORTED.
=== END COPILOT FILE RULES ===
*/

// === Tile.js: Portal-Based Rendering Version ===
import React, { useRef } from 'react';
import pcModule from './Tile.pc.module.css';
import mobileModule from './Tile.mobile.module.css';
import useSelectedCssModule from '../hooks/useSelectedCssModule';
import { CHESS_PIECES, COLOR_PIECE } from '../utils/gameUtils';
import { logDomEvent, isLoggerPortEnabled } from '../utils/spaLogger';

function serializeDom(node) {
  if (!node) return '';
  return node.outerHTML;
}

function Tile({ tile, highlight, solveMoveHint, showSolvedIndicator, shimmerRed, fileRank, style, onClick }) {
  const tileRef = useRef(null);
  const styles = useSelectedCssModule(pcModule, mobileModule);

  // Debug: Log indicator values only on click
  function debugLogIndicatorValues() {
    if (typeof window !== 'undefined') {
      console.log('[Tile.js] Click:', {
        fileRank,
        tileType: tile ? tile.type : null,
        showSolvedIndicator,
        isSolved: tile ? tile.isSolved : null,
        tile,
      });
    }
  }

  // Log event handler
  async function handleLogEvent(eventType) {
    const domNode = tileRef.current;
    const domHtml = serializeDom(domNode);
    const element = domNode ? (domNode.className || domNode.tagName) : 'unknown';
    const parent = domNode && domNode.parentElement ? (domNode.parentElement.className || domNode.parentElement.tagName) : 'unknown';
    if (isLoggerPortEnabled('Tile')) {
      await logDomEvent(
        'Tile',
        eventType,
        element,
        domHtml,
        domNode,
        parent
      );
    }
  }

  // Hover logic
  let hoverTimeout = null;
  function handleMouseEnter() {
    hoverTimeout = setTimeout(() => {
      handleLogEvent('hover');
    }, 400);
  }
  function handleMouseLeave() {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      hoverTimeout = null;
    }
  }
  function handleClick(e) {
    debugLogIndicatorValues();
    handleLogEvent('click');
    if (typeof onClick === 'function') {
      onClick(fileRank, e);
    }
  }

  // Only render color or piece tiles (empty handled by VortexIndicator)
  if (!tile) return null;

  if (tile.type === 'color') {
    return (
      <span
        ref={tileRef}
        className={
          `${styles.colorPiece} ${styles[tile.color]}` +
          (shimmerRed ? ' ' + styles.shimmerRed : '')
        }
        role="img"
        aria-label={`Color ${tile.color.charAt(0).toUpperCase() + tile.color.slice(1)}${fileRank ? ` on ${fileRank}` : ''}`}
        title={`Color ${tile.color.charAt(0).toUpperCase() + tile.color.slice(1)}${fileRank ? ` on ${fileRank}` : ''}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
      >
        <span className={
          styles.colorPieceInner +
          (highlight ? ' ' + styles.shimmerBorder : '') +
          (solveMoveHint ? ' ' + styles.solveMoveHint : '')
        }>{String.fromCharCode(0x25A0)}</span>
        {showSolvedIndicator && tile.isSolved && (
          <span className={styles.solvedIndicator} title="Solved" />
        )}
        {showSolvedIndicator && !tile.isSolved && (
          <span className={styles.unsolvedIndicator} title="Unsolved" />
        )}
      </span>
    );
  }
  if (tile.type === 'piece') {
    const pieceDef = CHESS_PIECES[tile.pieceRef];
    const unicode = pieceDef ? pieceDef.unicode : '';
    const isWhite = tile.pieceColor === 'white';
    const pieceName = tile.displayName || (pieceDef ? pieceDef.name : tile.pieceRef);
    // Add pieceRef as a class (e.g., black_pawn, white_pawn)
    const pieceRefClass = styles[tile.pieceRef] || '';
    return (
      <span
        ref={tileRef}
        className={
          `${styles.chessPiece} ${styles.chessPieceCentered} ${isWhite ? styles.white : styles.black}` +
          (pieceRefClass ? ' ' + pieceRefClass : '') +
          (shimmerRed ? ' ' + styles.shimmerRed : '')
        }
        role="img"
        aria-label={`${pieceName}${fileRank ? ` on ${fileRank}` : ''}`}
        title={`${pieceName}${fileRank ? ` on ${fileRank}` : ''}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
      >
        <span className={
          styles.chessPieceInner +
          (highlight ? ' ' + styles.shimmerBorder : '') +
          (solveMoveHint ? ' ' + styles.solveMoveHint : '')
        }>{unicode}</span>
        {showSolvedIndicator && tile.isSolved && (
          <span className={styles.solvedIndicator} title="Solved" />
        )}
        {showSolvedIndicator && !tile.isSolved && (
          <span className={styles.unsolvedIndicator} title="Unsolved" />
        )}
      </span>
    );
  }
  return null;
}

export default Tile;
// === End Section: Tile.js (Portal-Based Rendering) ===
